import { CompanyNameSchema } from "@cwb/protocol";

export const COMPANY_NAME_STORAGE_KEY = "cwb.company-name.v1";

export interface CompanyStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export type FixCompanyNameResult =
  | { ok: true; companyName: string }
  | { ok: false; error: string };

/** Read only a value that still satisfies the current shared wire contract. */
export function readCompanyName(storage: Pick<CompanyStorage, "getItem">): string | null {
  let stored: string | null;
  try {
    stored = storage.getItem(COMPANY_NAME_STORAGE_KEY);
  } catch {
    return null;
  }
  if (stored === null) return null;
  const parsed = CompanyNameSchema.safeParse(stored);
  return parsed.success ? parsed.data : null;
}

/**
 * Persist the installation's first valid name. A valid existing identity wins
 * forever; the product deliberately exposes no rename or delete operation.
 */
export function fixCompanyName(storage: CompanyStorage, candidate: string): FixCompanyNameResult {
  const parsed = CompanyNameSchema.safeParse(candidate);
  if (!parsed.success) return { ok: false, error: "invalid company name" };

  const existing = readCompanyName(storage);
  if (existing !== null) {
    return existing === parsed.data
      ? { ok: true, companyName: existing }
      : { ok: false, error: "company name is already fixed" };
  }

  try {
    storage.setItem(COMPANY_NAME_STORAGE_KEY, parsed.data);
  } catch {
    return { ok: false, error: "company name could not be saved" };
  }
  return { ok: true, companyName: parsed.data };
}

export function readBrowserCompanyName(): string | null {
  if (typeof window === "undefined") return null;
  return readCompanyName(browserCompanyStorage);
}

let temporaryStorage = false;
let tabCompanyName: string | null = null;

const browserCompanyStorage: CompanyStorage = {
  getItem(key) {
    if (temporaryStorage && tabCompanyName !== null) return tabCompanyName;
    try {
      tabCompanyName = window.localStorage.getItem(key);
    } catch {
      temporaryStorage = true;
    }
    return tabCompanyName;
  },
  setItem(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      temporaryStorage = true;
    }
    tabCompanyName = value;
  },
};

/** The same first-name policy, with tab memory when persistence is unavailable. */
export function fixBrowserCompanyName(candidate: string): FixCompanyNameResult {
  return fixCompanyName(browserCompanyStorage, candidate);
}

export function companyStorageIsTemporary(): boolean {
  return temporaryStorage;
}
